<template>
  <div class="tour-manager">
    <!-- Botón flotante para iniciar el tour - DESHABILITADO: funcionalidad movida al TopBar -->
    <!--
    <v-btn
      v-if="showTourButton"
      class="tour-fab"
      color="primary"
      fab
      fixed
      bottom
      right
      @click="startTour"
      elevation="8"
    >
      <v-icon>mdi-help-circle</v-icon>
    </v-btn>
    -->

    <!-- Modal de bienvenida para nuevos usuarios -->
    <v-dialog
      v-model="showWelcomeModal"
      max-width="600"
      persistent
      scrollable
    >
      <v-card class="welcome-card">
        <v-card-title class="welcome-header">
          <v-icon color="white" size="48" class="mr-3">mdi-hand-wave</v-icon>
          <div class="welcome-title-content">
            <h2 class="text-h5 mb-1">¡Bienvenido a Trazalogis!</h2>
            <p class="text-subtitle-1 welcome-subtitle mb-0">
              {{ welcomeMessage }}
            </p>
          </div>
        </v-card-title>

        <v-card-text class="welcome-content">
          <div class="feature-list">
            <div class="feature-item" v-for="feature in userFeatures" :key="feature.icon">
              <v-icon color="primary" class="mr-3 feature-icon">{{ feature.icon }}</v-icon>
              <div class="feature-content">
                <h4 class="text-subtitle-1 mb-1 feature-title">{{ feature.title }}</h4>
                <p class="text-body-2 text-grey-600 mb-0 feature-description">{{ feature.description }}</p>
              </div>
            </div>
          </div>

          <v-alert
            type="info"
            variant="tonal"
            class="mt-4 tour-recommendation"
          >
            <v-icon slot="prepend">mdi-lightbulb-outline</v-icon>
            <div class="alert-content">
              Te recomendamos hacer el tour guiado para conocer todas las funcionalidades disponibles.
            </div>
          </v-alert>
        </v-card-text>

        <v-card-actions class="welcome-actions">
          <v-btn
            text
            @click="skipWelcome"
            color="grey"
            class="skip-btn"
          >
            Saltar
          </v-btn>
          
          <v-spacer></v-spacer>
          
          <v-btn
            color="primary"
            variant="elevated"
            @click="startTourFromWelcome"
            prepend-icon="mdi-play-circle"
            class="start-tour-btn"
          >
            Iniciar Tour
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Componente del tour guiado -->
    <GuidedTour
      :tour-steps="currentTourSteps"
      :is-active="isTourActive"
      @tour-completed="onTourCompleted"
      @tour-skipped="onTourSkipped"
      @step-changed="onStepChanged"
    />

    <!-- Snackbar para notificaciones -->
    <v-snackbar
      v-model="showNotification"
      :color="notificationColor"
      timeout="4000"
      top
    >
      {{ notificationMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="showNotification = false"
        >
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import GuidedTour from './GuidedTour.vue'
import { getTourByRole, hasCompletedTour, markTourAsCompleted, resetTour } from './tourConfigs.js'
import { saludWelcome } from './tourConfigsSalud.js'

export default {
  name: 'TourManager',
  components: {
    GuidedTour
  },
  data() {
    return {
      isTourActive: false,
      showWelcomeModal: false,
      showNotification: false,
      notificationMessage: '',
      notificationColor: 'success',
      currentTourSteps: [],
      isFirstLogin: false
    }
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('tour', ['shouldStartTour']),
    
    showTourButton() {
      return this.currentUser && !this.isTourActive
    },

    // Perfil de tour: por usuario para el revisor GS1, por rol para el resto.
    // Se usa como clave de localStorage para no compartir el progreso con admin.
    isGs1Reviewer() {
      return this.currentUser && String(this.currentUser.username || '').toLowerCase() === 'revisorgs1'
    },

    tourProfile() {
      if (!this.currentUser) return 'viewer'
      if (this.isGs1Reviewer) return 'revisorgs1'
      return (this.sectorSalud ? 'salud-' : '') + this.currentUser.role
    },

    sectorSalud() {
      try { return !!this.$store.getters['edition/sectorSalud'] } catch (e) { return false }
    },

    welcomeMessage() {
      if (!this.currentUser) return ''

      if (this.isGs1Reviewer) {
        return 'Bienvenido nuevamente, revisor de GS1. Este recorrido cubre exclusivamente los PUNTOS OBSERVADOS en la primera evaluación (31/07/2026), en el orden del checklist: cada paso muestra la observación original, la corrección implementada y cómo verificarla con códigos de prueba listos para usar. Puede probar con sus propios códigos y repetir el recorrido desde el botón de ayuda cuando lo necesite.'
      }

      if (this.sectorSalud && saludWelcome[this.currentUser.role]) {
        return saludWelcome[this.currentUser.role].message
      }

      const messages = {
        admin: 'Como administrador, tienes acceso completo. Te guiaremos para poner en marcha tu bodega paso a paso, desde cero.',
        warehouse_manager: 'Como Jefe de Bodega, gestionas la operación completa: equipo, recepción, inventario y despachos. Te mostraremos todo en orden.',
        receiving_operator: 'Como Operario de Recepción, te mostraremos cómo contar y recibir mercancía de forma rápida y precisa.',
        picker: 'Como Picker, podrás gestionar tus tareas de recolección de manera eficiente, con rutas optimizadas.',
        salesperson: 'Como Vendedor, podrás crear y consultar pedidos y revisar la disponibilidad de productos.',
        operator: 'Como operario, podrás gestionar tus tareas de picking de manera eficiente.',
        viewer: 'Como visualizador, puedes consultar información y reportes del sistema.'
      }

      return messages[this.currentUser.role] || messages.admin
    },
    
    userFeatures() {
      if (!this.currentUser) return []

      if (this.isGs1Reviewer) {
        return [
          {
            icon: 'mdi-check-decagram',
            title: 'Sección 1 · Claves',
            description: 'GTIN solo numérico, lote máx. 20, SSCC correlativo (mín. 2 + peso), GLN, GSIN y AIs 90-99'
          },
          {
            icon: 'mdi-barcode-scan',
            title: 'Secciones 2 y 3 · Simbologías y FNC1',
            description: 'SKU=GTIN completo, DataBar Expandido Apilado, UPC-E, GS1-128 con ]C1 y FNC1 visible ⟨FNC1·GS⟩'
          },
          {
            icon: 'mdi-file-chart',
            title: 'Sección 4 · Reportes y documentos',
            description: 'Excel entrada/salida con origen/destino, (01)/(02) a 14 dígitos, filtros ágiles, packing lists separados'
          },
          {
            icon: 'mdi-printer',
            title: 'Sección 3.6 · Muestras físicas',
            description: 'Set de simbologías impreso en etiqueta autoadhesiva, pre-validado con BWIPP'
          }
        ]
      }

      if (this.sectorSalud && saludWelcome[this.currentUser.role]) {
        return saludWelcome[this.currentUser.role].features
      }

      const features = {
        admin: [
          {
            icon: 'mdi-account-group',
            title: 'Gestión de Usuarios',
            description: 'Administra tu equipo y asigna roles específicos'
          },
          {
            icon: 'mdi-warehouse',
            title: 'Control de Inventario',
            description: 'Gestiona productos, categorías y stock en tiempo real'
          },
          {
            icon: 'mdi-clipboard-list',
            title: 'Órdenes y Picking',
            description: 'Crea órdenes y asigna tareas de recolección'
          },
          {
            icon: 'mdi-chart-line',
            title: 'Analytics y Reportes',
            description: 'Visualiza KPIs y métricas de rendimiento'
          }
        ],
        operator: [
          {
            icon: 'mdi-clipboard-check',
            title: 'Tareas de Picking',
            description: 'Recibe y completa tareas de recolección asignadas'
          },
          {
            icon: 'mdi-map-marker-path',
            title: 'Rutas Optimizadas',
            description: 'Sigue rutas calculadas para máxima eficiencia'
          },
          {
            icon: 'mdi-package-variant',
            title: 'Consulta de Inventario',
            description: 'Verifica ubicaciones y disponibilidad de productos'
          },
          {
            icon: 'mdi-chart-box',
            title: 'Estadísticas Personales',
            description: 'Monitorea tu productividad y rendimiento'
          }
        ],
        viewer: [
          {
            icon: 'mdi-eye',
            title: 'Consulta de Información',
            description: 'Accede a datos de inventario y órdenes'
          },
          {
            icon: 'mdi-chart-timeline-variant',
            title: 'Reportes y Analytics',
            description: 'Visualiza métricas y reportes del sistema'
          },
          {
            icon: 'mdi-history',
            title: 'Trazabilidad',
            description: 'Consulta el historial de movimientos'
          }
        ]
      }

      // Los roles granulares reutilizan el set de features más cercano a su trabajo.
      features.warehouse_manager = features.admin
      features.picker = features.operator
      features.receiving_operator = features.operator
      features.salesperson = features.viewer

      return features[this.currentUser.role] || features.viewer
    }
  },
  watch: {
    currentUser: {
      handler(newUser, oldUser) {
        if (newUser && (!oldUser || oldUser.id !== newUser.id)) {
          this.checkFirstLogin()
        }
      },
      immediate: true
    },
    shouldStartTour(newVal) {
      if (newVal) {
        this.startTour()
        // Resetear la flag después de iniciar el tour
        this.$store.dispatch('tour/resetTourFlags')
      }
    }
  },
  mounted() {
    this.setupTourManager()
  },
  methods: {
    setupTourManager() {
      // En Vue 3, $root.$on no existe, usamos un enfoque alternativo
      // o emitimos eventos directamente desde los componentes
      
      // Verificar si es el primer login
      this.checkFirstLogin()
    },
    
    checkFirstLogin() {
      if (!this.currentUser) return
      
      try {
        const hasSeenWelcome = localStorage.getItem(`welcome-seen-${this.tourProfile}`)
        const hasCompletedUserTour = hasCompletedTour(this.tourProfile)
        
        // Mostrar bienvenida si es la primera vez o no ha completado el tour
        if (!hasSeenWelcome || !hasCompletedUserTour) {
          this.isFirstLogin = true
          setTimeout(() => {
            this.showWelcomeModal = true
          }, 1500) // Aumentar delay para asegurar que Vuetify esté listo
        }
      } catch (error) {
        console.warn('Error checking first login:', error)
      }
    },
    
    startTour() {
      if (!this.currentUser) {
        this.showNotificationMessage('Debes estar logueado para iniciar el tour', 'error')
        return
      }
      
      try {
        this.currentTourSteps = getTourByRole(this.currentUser.role, this.currentUser.username, this.sectorSalud)
        
        // Verificar que hay pasos en el tour
        if (!this.currentTourSteps || this.currentTourSteps.length === 0) {
          this.showNotificationMessage('No hay tour disponible para tu rol', 'warning')
          return
        }
        
        // Cerrar cualquier modal abierto antes de iniciar el tour
        this.showWelcomeModal = false
        
        // Esperar un momento para que se cierre el modal
        this.$nextTick(() => {
          this.isTourActive = true
          this.$store.dispatch('tour/setTourActive', true)
          this.showNotificationMessage('¡Tour iniciado! Sigue las instrucciones para conocer el sistema', 'info')
        })
      } catch (error) {
        console.error('Error starting tour:', error)
        this.showNotificationMessage('Error al iniciar el tour', 'error')
      }
    },
    
    startTourFromWelcome() {
      try {
        localStorage.setItem(`welcome-seen-${this.tourProfile}`, 'true')
        this.startTour()
      } catch (error) {
        console.error('Error starting tour from welcome:', error)
        this.showNotificationMessage('Error al iniciar el tour', 'error')
      }
    },
    
    skipWelcome() {
      try {
        localStorage.setItem(`welcome-seen-${this.tourProfile}`, 'true')
        this.showWelcomeModal = false
        this.showNotificationMessage('Puedes iniciar el tour en cualquier momento desde el botón de ayuda', 'info')
      } catch (error) {
        console.error('Error skipping welcome:', error)
        this.showWelcomeModal = false
      }
    },
    
    onTourCompleted() {
      this.isTourActive = false
      this.$store.dispatch('tour/setTourActive', false)
      try {
        markTourAsCompleted(this.tourProfile)
        this.showNotificationMessage('¡Felicitaciones! Has completado el tour del sistema', 'success')
        
        // Opcional: Mostrar modal de finalización con recursos adicionales
        this.showCompletionModal()
      } catch (error) {
        console.error('Error completing tour:', error)
        this.showNotificationMessage('Tour completado', 'success')
      }
    },
    
    onTourSkipped() {
      this.isTourActive = false
      this.$store.dispatch('tour/setTourActive', false)
      this.showNotificationMessage('Tour cancelado. Puedes reiniciarlo cuando quieras', 'warning')
    },
    
    onStepChanged(stepIndex) {
      // Opcional: Tracking de progreso del tour
      //console.log(`Tour step changed to: ${stepIndex}`)
    },
    
    resetUserTour() {
      if (!this.currentUser) return
      
      try {
        resetTour(this.tourProfile)
        localStorage.removeItem(`welcome-seen-${this.tourProfile}`)
        this.showNotificationMessage('Tour reseteado. Puedes iniciarlo nuevamente', 'info')
      } catch (error) {
        console.error('Error resetting tour:', error)
        this.showNotificationMessage('Error al resetear el tour', 'error')
      }
    },
    
    showCompletionModal() {
      // Implementar modal de finalización con recursos adicionales
      this.$nextTick(() => {
        // Aquí podrías mostrar un modal con:
        // - Enlaces a documentación
        // - Videos tutoriales
        // - Contacto de soporte
        // - Certificado de completación
      })
    },
    
    showNotificationMessage(message, color = 'info') {
      this.notificationMessage = message
      this.notificationColor = color
      this.showNotification = true
    }
  },
  
  beforeUnmount() {
    // En Vue 3, $root.$off no existe
    // Los event listeners se limpian automáticamente
  }
}
</script>

<style scoped>
.tour-manager {
  position: relative;
}

/* Estilos del botón flotante - DESHABILITADOS
.tour-fab {
  z-index: 1000;
  margin-bottom: 16px;
  margin-right: 16px;
}

.tour-fab:hover {
  transform: scale(1.1);
  transition: transform 0.2s ease;
}
*/

.welcome-card {
  overflow: hidden;
  max-height: 90vh;
}

.welcome-header {
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
  color: white;
  padding: 24px;
  align-items: flex-start;
  min-height: auto;
}

.welcome-title-content {
  flex: 1;
  min-width: 0; /* Permite que el texto se ajuste */
}

.welcome-subtitle {
  color: rgba(255, 255, 255, 0.9) !important;
  line-height: 1.4;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.welcome-content {
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
}

.feature-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  padding: 16px;
  border-radius: 8px;
  background: #f8f9fa;
  transition: background-color 0.2s ease;
}

.feature-item:hover {
  background: #e3f2fd;
}

.feature-icon {
  margin-top: 2px; /* Alinear con el texto */
  flex-shrink: 0;
}

.feature-content {
  flex: 1;
  min-width: 0;
}

.feature-title {
  font-weight: 600;
  color: #1976d2;
  margin-bottom: 4px;
}

.feature-description {
  line-height: 1.4;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.tour-recommendation {
  border-radius: 8px;
}

.alert-content {
  line-height: 1.4;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.welcome-actions {
  padding: 16px 24px 24px 24px;
  background: #fafafa;
  flex-shrink: 0;
}

.skip-btn {
  min-width: 80px;
}

.start-tour-btn {
  min-width: 140px;
}

/* Animaciones */
.welcome-card {
  animation: slideInUp 0.4s ease-out;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 768px) {
  /* Botón flotante deshabilitado
  .tour-fab {
    margin-bottom: 80px;
  }
  */
  
  .welcome-card {
    max-height: 95vh;
    margin: 8px;
  }
  
  .welcome-header {
    padding: 20px;
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .welcome-title-content h2 {
    font-size: 1.3rem;
  }
  
  .welcome-subtitle {
    font-size: 0.9rem;
  }
  
  .welcome-content {
    padding: 20px;
    max-height: 65vh;
  }
  
  .feature-item {
    padding: 12px;
    flex-direction: column;
    text-align: center;
    gap: 8px;
  }
  
  .feature-icon {
    margin: 0;
    align-self: center;
  }
  
  .feature-title {
    font-size: 1rem;
  }
  
  .feature-description {
    font-size: 0.85rem;
  }
  
  .welcome-actions {
    padding: 16px 20px 20px 20px;
    flex-direction: column;
    gap: 12px;
  }
  
  .skip-btn,
  .start-tour-btn {
    width: 100%;
    min-width: auto;
  }
}

@media (max-width: 480px) {
  .welcome-card {
    max-height: 98vh;
    margin: 4px;
  }
  
  .welcome-header {
    padding: 16px;
  }
  
  .welcome-title-content h2 {
    font-size: 1.2rem;
  }
  
  .welcome-subtitle {
    font-size: 0.85rem;
  }
  
  .welcome-content {
    padding: 16px;
    max-height: 70vh;
  }
  
  .feature-item {
    padding: 10px;
  }
  
  .feature-title {
    font-size: 0.95rem;
  }
  
  .feature-description {
    font-size: 0.8rem;
  }
}

/* Tema oscuro */
.theme--dark .feature-item {
  background: #2d2d2d;
}

.theme--dark .feature-item:hover {
  background: #1e3a8a;
}

.theme--dark .welcome-actions {
  background: #1e1e1e;
}

/* Asegurar que el scroll funcione correctamente */
.welcome-content::-webkit-scrollbar {
  width: 6px;
}

.welcome-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.welcome-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.welcome-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style> 